import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { specialConversions } from '../../../../utils/unitConversions/number/numberUtils';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import debounce from 'lodash/debounce';

const formatRomanNumeral = (romanValue) => {
  // Match consecutive identical characters
  const groups = romanValue.match(/(.)\1*/g) || [];
  return groups.map(group => {
    return group.length > 3 
      ? `${group[0]}×${group.length}`  // For repeated characters (e.g., "M×25")
      : group  // Keep as is for 3 or fewer repetitions (e.g., "III")
  }).join(' + ');
};

const ConversionExplanationDecimalToAll = ({
  conversionType,
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode,
  isExample,
  isEncodingMode,
  onEncodingModeToggle,
}) => {
  // State Management
  const [showFunFact, setShowFunFact] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [quizNumber, setQuizNumber] = useState(null);
  const [funFactKey, setFunFactKey] = useState(0);
  const [currentFunFact, setCurrentFunFact] = useState('');
  const [showHexDigits, setShowHexDigits] = useState(false);
  const [showBase36Digits, setShowBase36Digits] = useState(false);
  const [showBase64Digits, setShowBase64Digits] = useState(false);
  const [showBinaryConversion, setShowBinaryConversion] = useState(false);
  const [showBase64Table, setShowBase64Table] = useState(false);
  const [showEncodingProcess, setShowEncodingProcess] = useState(false);
  const [isPadded, setIsPadded] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stepsReady, setStepsReady] = useState(false);
  const [steps, setSteps] = useState([]);

  // Fun Facts
  const funFacts = [
    "The Romans didn't have a symbol for zero!",
    "The largest number that can be represented in Roman numerals is 3,999 (MMMCMXCIX).",
    "The Roman numeral system is a non-positional number system.",
    "In medieval times, the letter 'j' was sometimes used instead of 'i' in Roman numerals.",
    "The use of Roman numerals continued long after the fall of the Roman Empire.",
  ];

  const getRandomFunFact = () => {
    return funFacts[Math.floor(Math.random() * funFacts.length)];
  };

  useEffect(() => {
    setCurrentFunFact(getRandomFunFact());
  }, []);

  // Quiz Handling
  const handleQuizSubmit = () => {
    const correctAnswer =
      inputValue !== null ? parseFloat(inputValue).toExponential() : '4.2314e+1';
    setQuizResult(
      quizAnswer.replace(/\s/g, '').toLowerCase() ===
        correctAnswer.replace(/\s/g, '').toLowerCase()
    );
  };

  // Render Therefore Statement
  const renderThereforeStatement = (fromValue, fromUnit, toValue, toUnit) => {
    if (toUnit === 'Base-64') {
      const trimmedResult = toValue.replace(/^A+/, '') || 'A';
      return (
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{fromValue}</span> ({fromUnit}) ={' '}
          <span className="text-green-500">{trimmedResult}</span> ({toUnit})
        </p>
      );
    }

    return (
      <p className="mt-4 font-bold break-words">
        Therefore,{' '}
        <span className="text-blue-500 break-all">{fromValue}</span> ({fromUnit}) ={' '}
        <span className="text-green-500 break-all">{toValue}</span> ({toUnit})
      </p>
    );
  };

  // Render How It Works
  const renderHowItWorks = () => {
    return (
      <div className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-semibold">
            How {fromUnit} to {toUnit} Conversion Works
          </h3>
          {toUnit === 'Base-64' && (
            <motion.button
              onClick={onEncodingModeToggle}
              className={`flex items-center px-3 py-1 rounded-full ${
                darkMode
                  ? isEncodingMode
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-300'
                  : isEncodingMode
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-200 text-gray-600'
              } transition-colors duration-200`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isEncodingMode ? (
                <>
                  <FaLock className="mr-2" />
                  <span className="text-sm">Encoding</span>
                </>
              ) : (
                <>
                  <FaLockOpen className="mr-2" />
                  <span className="text-sm">Standard</span>
                </>
              )}
            </motion.button>
          )}
        </div>
        <p className="mb-4 text-gray-600 dark:text-gray-400">
          {toUnit === 'Base-64'
            ? isEncodingMode
              ? "Base64 encoding converts binary data into a text format using 64 characters. It's commonly used for encoding binary data in text-based protocols."
              : `This explanation shows how to convert from ${fromUnit} to ${toUnit} using standard conversion techniques.`
            : `This explanation shows how to convert from ${fromUnit} to ${toUnit} using standard conversion techniques.`}
        </p>
      </div>
    );
  };
  

  const getExplanation = () => {
    const isExample = inputValue === null;
    const exampleValue = isExample ? (fromUnit === 'Decimal' ? '42314.159' : '42') : inputValue;

    const renderExampleOrConversionText = () => (
      isExample ? (
        <p className="mb-4 text-yellow-600 italic">
          Example: Let's convert the {fromUnit.toLowerCase()} number {exampleValue} to {toUnit}.
        </p>
      ) : (
        <p className="mb-4 text-green-600">
          Your conversion: {inputValue} ({fromUnit}) to {toUnit}
        </p>
      )
    );

    const renderBinaryRepresentation = (binaryString) => {
      if (conversionType === 'number' && fromUnit === 'Decimal' && toUnit === 'Base-64') {
        return (
          <div className="mt-2 mb-4">
            <p className="mb-4">
              {isEncodingMode 
                ? "Base-64 encoding converts binary data into text by grouping bits and mapping them to printable characters. Let's see how this works step by step:"
                : "To convert a decimal number to base-64, we repeatedly divide the number by 64 and keep track of the remainders. The remainders correspond to Base-64 digits based on the following mapping:"}
            </p>
          </div>
        );
      }
      return null;
    };

    if (conversionType === 'number' && fromUnit === 'Decimal' && (toUnit === 'Binary' || toUnit === 'Octal')) {
      const decimalValue = exampleValue ? parseInt(exampleValue) : 42;
      const isExample = !inputValue;
      let convertedValue;
      const base = toUnit === 'Binary' ? 2 : 8;

      convertedValue = decimalValue.toString(base);

      const steps = [];
      let remainingValue = decimalValue;
      let position = 0;

      while (remainingValue > 0) {
        const digit = remainingValue % base;
        steps.push({ position, digit, value: remainingValue });
        remainingValue = Math.floor(remainingValue / base);
        position++;
      }

      return (
        <div className="space-y-4">
          {renderHowItWorks()}
          {renderExampleOrConversionText()}
       
          <p className="mb-4">
            To convert a decimal number to {toUnit.toLowerCase()}, we repeatedly divide the number by {base} and keep track of the remainders.
            The remainders, read from bottom to top, form the {toUnit.toLowerCase()} number.
          </p>
          {steps.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">Step</th>
                      <th className="px-4 py-2">Calculation</th>
                      <th className="px-4 py-2">Remainder ({toUnit} Digit)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.reverse().map((step, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-white') : (darkMode ? 'bg-gray-800' : 'bg-gray-50')}
                      >
                        <td className="py-2 px-4 border-b whitespace-nowrap">{steps.length - index}</td>
                        <td className="py-2 px-4 border-b break-all">
                          {step.value} ÷ {base} = {Math.floor(step.value / base)} remainder {step.digit}
                        </td>
                        <td className="py-2 px-4 border-b whitespace-nowrap">
                          <span
                            className={`inline-flex items-center justify-center w-8 h-8 
                              ${step.digit === 1 
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' 
                                : 'bg-gray-100 dark:bg-gray-700'} 
                              rounded-lg font-mono text-lg font-semibold
                              ${step.digit === 1 
                                ? 'border-2 border-blue-300 dark:border-blue-700' 
                                : 'border-2 border-gray-300 dark:border-gray-600'}
                              transform transition-all duration-200 hover:scale-110
                              shadow-sm hover:shadow-md`}
                          >
                            {step.digit}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="font-semibold">Final Step:</span>
                </div>
                <p className="mb-2">
                  Reading the remainders from bottom to top gives us the {toUnit.toLowerCase()} number:
                </p>
                <div className="text-center py-2 px-4 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <span className="font-mono text-xl font-bold text-blue-700 dark:text-blue-300">
                    {convertedValue}
                  </span>
                </div>
              </div>
              {renderThereforeStatement(decimalValue, fromUnit, convertedValue, toUnit)}
            </>
          ) : (
            <p className="text-red-500">Please enter a valid positive integer to see the conversion process.</p>
          )}
        </div>
      );
    } else if (conversionType === 'number' && fromUnit === 'Binary' && toUnit === 'Hexadecimal') {
      const binaryValue = exampleValue ? exampleValue.replace(/[^01]/g, '') : '101010';
      const isExample = !inputValue;
      let hexValue;

      hexValue = parseInt(binaryValue, 2).toString(16).toUpperCase();

      const paddedBinary = binaryValue.padStart(Math.ceil(binaryValue.length / 4) * 4, '0');

      const steps = [];
      for (let i = 0; i < paddedBinary.length; i += 4) {
        const group = paddedBinary.slice(i, i + 4);
        const decimal = parseInt(group, 2);
        const hex = decimal.toString(16).toUpperCase();
        steps.push({ group, decimal, hex });
      }

      return (
        <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">How Binary to Hexadecimal Conversion Works</h3>
            {toUnit === 'Base-64' && (
              <motion.button
                onClick={onEncodingModeToggle}
                className={`flex items-center px-3 py-1 rounded-full ${
                  darkMode
                    ? isEncodingMode
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300'
                    : isEncodingMode
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-200 text-gray-600'
                } transition-colors duration-200`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isEncodingMode ? (
                  <>
                    <FaLock className="mr-2" />
                    <span className="text-sm">Encoding</span>
                  </>
                ) : (
                  <>
                    <FaLockOpen className="mr-2" />
                    <span className="text-sm">Standard</span>
                  </>
                )}
              </motion.button>
            )}
          </div>

          {renderExampleOrConversionText()}
          <p className="mb-4">
            To convert a binary number to hexadecimal, we group the binary digits into sets of 4 (padding with leading zeros if necessary),
            then convert each group to its hexadecimal equivalent.
          </p>
          {steps.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">Binary Group</th>
                      <th className="px-4 py-2">Decimal Value</th>
                      <th className="px-4 py-2">Hexadecimal Digit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((step, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                      >
                        <td className="border px-4 py-2">{step.group}</td>
                        <td className="border px-4 py-2">{step.decimal}</td>
                        <td className="border px-4 py-2">
                          <span className="font-bold text-purple-500">{step.hex}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Combining the hexadecimal digits gives us the hexadecimal number:
                <span className="font-bold text-purple-500"> {hexValue}</span>
              </p>
              {renderThereforeStatement(binaryValue, fromUnit, hexValue, toUnit)}
            </>
          ) : (
            <p className="text-red-500">Please enter a valid binary number to see the conversion process.</p>
          )}
        </div>
      );
    } else if (conversionType === 'number' && fromUnit === 'Decimal' && toUnit === 'Hexadecimal') {
      const decimalValue = exampleValue ? parseInt(exampleValue) : 42;
      const isExample = !inputValue;
      let hexValue = decimalValue.toString(16).toUpperCase();

      const hexDigits = '0123456789ABCDEF'.split('');

      const HexDigitGrid = () => (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 overflow-hidden"
        >
          <h4 className="text-lg font-semibold mb-2">Hexadecimal Digits:</h4>
          <div className="grid grid-cols-4 gap-2 md:grid-cols-8 lg:grid-cols-16">
            {hexDigits.map((digit, index) => (
              <div
                key={digit}
                className={`p-2 text-center rounded ${index < 10 ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                  } ${darkMode ? 'dark:bg-gray-700 dark:text-white' : ''}`}
              >
                <span className="font-bold">{digit}</span>
                <span className="text-xs block">{index}</span>
              </div>
            ))}
          </div>
        </motion.div>
      );

      const steps = [];
      let remainingValue = decimalValue;
      let position = 0;

      while (remainingValue > 0) {
        const remainder = remainingValue % 16;
        const hexDigit = hexDigits[remainder];
        steps.push({ position, remainder, hexDigit, value: remainingValue });
        remainingValue = Math.floor(remainingValue / 16);
        position++;
      }

      return (
        <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">How Decimal to Hexadecimal Conversion Works</h3>
            {toUnit === 'Base-64' && (
              <motion.button
                onClick={onEncodingModeToggle}
                className={`flex items-center px-3 py-1 rounded-full ${
                  darkMode
                    ? isEncodingMode
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300'
                    : isEncodingMode
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-200 text-gray-600'
                } transition-colors duration-200`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isEncodingMode ? (
                  <>
                    <FaLock className="mr-2" />
                    <span className="text-sm">Encoding</span>
                  </>
                ) : (
                  <>
                    <FaLockOpen className="mr-2" />
                    <span className="text-sm">Standard</span>
                  </>
                )}
              </motion.button>
            )}
          </div>

          {renderExampleOrConversionText()}
          <p className="mb-4">
            To convert a decimal number to hexadecimal, we repeatedly divide the number by 16 and keep track of the remainders.
            The remainders, converted to hexadecimal digits and read from bottom to top, form the hexadecimal number.
          </p>
          <button
            onClick={() => setShowHexDigits(!showHexDigits)}
            className={`mb-4 px-4 py-2 rounded ${darkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
              } transition-colors duration-200`}
          >
            {showHexDigits ? 'Hide' : 'Show'} Hexadecimal Digits
          </button>
          <AnimatePresence>
            {showHexDigits && <HexDigitGrid />}
          </AnimatePresence>
          {steps.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">Step</th>
                      <th className="px-4 py-2">Calculation</th>
                      <th className="px-4 py-2">Remainder</th>
                      <th className="px-4 py-2">Hex Digit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.reverse().map((step, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-white') : (darkMode ? 'bg-gray-800' : 'bg-gray-50')}
                      >
                        <td className="py-2 px-4 border-b whitespace-nowrap">{steps.length - index}</td>
                        <td className="py-2 px-4 border-b break-all">{step.value} ÷ 16 = {Math.floor(step.value / 16)} remainder {step.remainder}</td>
                        <td className="py-2 px-4 border-b whitespace-nowrap">{step.remainder}</td>
                        <td className="py-2 px-4 border-b whitespace-nowrap">{step.hexDigit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Reading the hex digits from bottom to top gives us the hexadecimal number:
                <span className="font-bold text-purple-500"> {hexValue}</span>
              </p>
              {renderThereforeStatement(decimalValue, fromUnit, hexValue, toUnit)}
            </>
          ) : (
            <p className="text-red-500">Please enter a valid positive integer to see the conversion process.</p>
          )}
        </div>
      );
    } else if (conversionType === 'number' && fromUnit === 'Binary' && toUnit === 'Base-36') {
      const binaryValue = exampleValue ? exampleValue.replace(/[^01]/g, '') : '101010';
      const isExample = !inputValue;
      const decimalValue = parseInt(binaryValue, 2);
      let base36Value = decimalValue.toString(36).toUpperCase();

      const steps = [];
      let remainingValue = decimalValue;
      let position = 0;

      while (remainingValue > 0) {
        const remainder = remainingValue % 36;
        const base36Digit = remainder.toString(36).toUpperCase();
        steps.push({ position, remainder, base36Digit, value: remainingValue });
        remainingValue = Math.floor(remainingValue / 36);
        position++;
      }

      return (
        <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">How Binary to Base-36 Conversion Works</h3>
            {toUnit === 'Base-64' && (
              <motion.button
                onClick={onEncodingModeToggle}
                className={`flex items-center px-3 py-1 rounded-full ${
                  darkMode
                    ? isEncodingMode
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300'
                    : isEncodingMode
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-200 text-gray-600'
                } transition-colors duration-200`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isEncodingMode ? (
                  <>
                    <FaLock className="mr-2" />
                    <span className="text-sm">Encoding</span>
                  </>
                ) : (
                  <>
                    <FaLockOpen className="mr-2" />
                    <span className="text-sm">Standard</span>
                  </>
                )}
              </motion.button>
            )}
          </div>

          {renderExampleOrConversionText()}
          <p className="mb-4">
            To convert a binary number to base-36, we first convert the binary to decimal, then convert the decimal to base-36.
            We repeatedly divide the decimal number by 36 and keep track of the remainders.
            The remainders, converted to base-36 digits (0-9, A-Z) and read from bottom to top, form the base-36 number.
          </p>
          <p className="mb-4">
            Binary {binaryValue} is equal to decimal {decimalValue}.
          </p>
          {steps.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">Step</th>
                      <th className="px-4 py-2">Calculation</th>
                      <th className="px-4 py-2">Remainder</th>
                      <th className="px-4 py-2">Base-36 Digit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.reverse().map((step, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                      >
                        <td className="border px-4 py-2">{index + 1}</td>
                        <td className="border px-4 py-2">{step.value} ÷ 36 = {Math.floor(step.value / 36)} remainder {step.remainder}</td>
                        <td className="border px-4 py-2">{step.remainder}</td>
                        <td className="border px-4 py-2">
                          <span className="font-bold text-indigo-500">{step.base36Digit}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Reading the base-36 digits from bottom to top gives us the base-36 number:
                <span className="font-bold text-indigo-500"> {base36Value}</span>
              </p>
              {renderThereforeStatement(binaryValue, fromUnit, base36Value, toUnit)}
            </>
          ) : (
            <p className="text-red-500">Please enter a valid binary number to see the conversion process.</p>
          )}
        </div>
      );
    } else if (conversionType === 'number' && fromUnit === 'Decimal' && toUnit === 'Base-36') {
      const decimalValue = exampleValue ? parseInt(exampleValue) : 42;
      const isExample = !inputValue;
      let base36Value = decimalValue.toString(36).toUpperCase();

      const base36Digits = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

      const Base36DigitGrid = () => (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2">Base-36 Digits:</h4>
          <div className="grid grid-cols-6 gap-2 md:grid-cols-9 lg:grid-cols-12">
            {base36Digits.map((digit, index) => (
              <div
                key={digit}
                className={`p-2 text-center rounded ${index < 10 ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                  } ${darkMode ? 'dark:bg-gray-700 dark:text-white' : ''}`}
              >
                <span className="font-bold">{digit}</span>
                <span className="text-xs block">{index}</span>
              </div>
            ))}
          </div>
        </div>
      );

      const steps = [];
      let remainingValue = decimalValue;
      let position = 0;

      while (remainingValue > 0) {
        const remainder = remainingValue % 36;
        const base36Digit = base36Digits[remainder];
        steps.push({ position, remainder, base36Digit, value: remainingValue });
        remainingValue = Math.floor(remainingValue / 36);
        position++;
      }

      return (
        <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          {renderHowItWorks()}
          {renderExampleOrConversionText()}
          


          <p className="mb-4">
            To convert a decimal number to base-36, we repeatedly divide the number by 36 and keep track of the remainders.
            The remainders, converted to base-36 digits and read from bottom to top, form the base-36 number.
          </p>
          <button
            onClick={() => setShowBase36Digits(!showBase36Digits)}
            className={`mb-4 px-4 py-2 rounded ${darkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
              } transition-colors duration-200`}
          >
            {showBase36Digits ? 'Hide' : 'Show'} Base-36 Digits
          </button>
          {showBase36Digits && <Base36DigitGrid />}
          {steps.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">Step</th>
                      <th className="px-4 py-2">Calculation</th>
                      <th className="px-4 py-2">Remainder</th>
                      <th className="px-4 py-2">Base-36 Digit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.reverse().map((step, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-white') : (darkMode ? 'bg-gray-800' : 'bg-gray-50')}
                      >
                        <td className="py-2 px-4 border-b whitespace-nowrap">{steps.length - index}</td>
                        <td className="py-2 px-4 border-b break-all">{step.value} ÷ 36 = {Math.floor(step.value / 36)} remainder {step.remainder}</td>
                        <td className="py-2 px-4 border-b whitespace-nowrap">{step.remainder}</td>
                        <td className="py-2 px-4 border-b whitespace-nowrap">{step.base36Digit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Reading the base-36 digits from bottom to top gives us the base-36 number:
                <span className="font-bold text-purple-500"> {base36Value}</span>
              </p>
              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2">Base-36 representation of {decimalValue}:</h4>
                <div className="flex flex-wrap items-center">
                  {base36Value.split('').map((digit, index) => (
                    <motion.div
                      key={index}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-10 h-10 m-1 flex items-center justify-center border-2 border-purple-500 text-purple-500 font-bold text-xl rounded-md"
                    >
                      {digit}
                    </motion.div>
                  ))}
                </div>
              </div>
              {renderThereforeStatement(decimalValue, fromUnit, base36Value, toUnit)}
            </>
          ) : (
            <p className="text-red-500">Please enter a valid positive integer to see the conversion process.</p>
          )}
        </div>
      );
    } else if (conversionType === 'number' && fromUnit === 'Decimal' && toUnit === 'Base-64') {


      let decimalValue = isExample ? 42314 : parseInt(inputValue, 10);
      if (isEncodingMode) {
        const decimalValue = exampleValue ? parseInt(exampleValue) : 42314;

        const ConversionStep = ({ step, title, description, content }) => (
          <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
            <h4 className="text-xl font-semibold mb-3 flex items-center text-gray-800 dark:text-gray-200">
              <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                {step}
              </span>
              {title}
            </h4>
            <p className="mb-4 text-gray-600 dark:text-gray-400">{description}</p>
            {content}
          </div>
        );

        const Base64EncodingTable = () => (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-6 overflow-hidden"
          >
            <h4 className="text-lg font-semibold mb-2">Base64 Encoding Table:</h4>
            <div className="grid grid-cols-8 gap-2 md:grid-cols-16">
              {'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'.split('').map((char, index) => (
                <div
                  key={char}
                  className={`p-2 text-center rounded ${index < 26
                    ? 'bg-blue-100 text-blue-800'
                    : index < 52
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                    } ${darkMode ? 'dark:bg-gray-700 dark:text-white' : ''}`}
                >
                  <span className="font-bold">{char}</span>
                  <span className="text-xs block">{index}</span>
                </div>
              ))}
            </div>
          </motion.div>
        );

        const byteArray = [
          (decimalValue >> 24) & 0xFF,
          (decimalValue >> 16) & 0xFF,
          (decimalValue >> 8) & 0xFF,
          decimalValue & 0xFF
        ].filter(byte => byte !== 0 || isPadded);
        
        const binaryString = isPadded
          ? decimalValue.toString(2).padStart(32, '0')
          : decimalValue.toString(2);
        
        const base64Value = btoa(String.fromCharCode.apply(null, byteArray));

        return (
          
          <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
       {renderHowItWorks()} 

        {renderExampleOrConversionText()}
        <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
    <div className="flex justify-between items-center mb-2">
      <h4 className="font-semibold">About Padding</h4>
      <button
        onClick={() => setIsPadded(!isPadded)}
        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
          darkMode
            ? isPadded
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300'
            : isPadded
            ? 'bg-blue-100 text-blue-800'
            : 'bg-gray-200 text-gray-600'
        }`}
      >
        Padding: {isPadded ? 'On' : 'Off'}
      </button>
    </div>
    <p className="text-sm text-gray-600 dark:text-gray-400">
      Padding ensures that the binary representation always has a fixed length (32 bits in this case). 
      This can be useful for consistent encoding and decoding, especially when dealing with protocols 
      that expect fixed-length data. However, for smaller numbers, padding may result in longer 
      Base64 strings. Toggle padding on/off to see the difference in the result.
    </p>
  </div>
        <ConversionStep
          step={1}
          title={`Convert decimal to ${isPadded ? '32-bit ' : ''}binary`}
          description={`We convert the decimal number to its ${isPadded ? '32-bit' : 'minimal'} binary representation${isPadded ? ', adding leading zeros if necessary' : ''}.`}
          content={
            <div className="font-mono">
              <div className="flex items-center mb-4">
                <span className="w-20">Decimal:</span>
                <span className="font-bold text-blue-600 dark:text-blue-400">{inputValue || '42314'}</span>
              </div>
              <div className="mb-2">
                <span className="w-20">Binary:</span>
              </div>
              <div className="text-center">
                <span className="text-green-600 dark:text-green-400 font-mono">{binaryString}</span>
              </div>
              {isPadded && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Note: We've padded the binary representation to 32 bits with leading zeros. This ensures consistent encoding for all numbers within the 32-bit range.
                </p>
              )}
            </div>
          }
        />
        <ConversionStep
          step={2}
          title="Group binary into bytes"
          description={`We group the ${isPadded ? '32-bit' : ''} binary number into ${isPadded ? 'four' : ''} 8-bit bytes for easier processing.`}
          content={
            <div className="font-mono text-center">
              <div className="flex flex-wrap justify-center items-center space-x-4 mb-4">
                {binaryString.match(/.{1,8}/g).map((byte, index) => (
                  <div key={index} className="mb-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <span className="text-purple-600 dark:text-purple-400 font-semibold">{byte.padStart(8, '0')}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Grouping into bytes helps us prepare for the next step and is a common practice in binary-to-text encodings.
                {!isPadded && " Note that the last byte may be partial if padding is off."}
              </p>
            </div>
          }
        />

            <ConversionStep
              step={3}
              title="Convert bytes to decimal values"
              description="We convert each byte to its decimal equivalent. This step bridges binary and Base64 representations."
              content={
                <div className="font-mono">
                  <div className="flex flex-wrap justify-center items-center space-x-4 mb-4">
                    {binaryString.match(/.{8}/g).map((byte, index) => (
                      <div key={index} className="mb-2 text-center">
                        <span className="block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-t-lg text-purple-600 dark:text-purple-400 font-semibold">
                          {byte}
                        </span>
                        <span className="block px-3 py-1 bg-blue-100 dark:bg-blue-900 rounded-b-lg text-blue-600 dark:text-blue-400 font-bold">
                          {parseInt(byte, 2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    These decimal values will be used in the final Base64 encoding step.
                  </p>
                </div>
              }
            />

            <ConversionStep
              step={4}
              title="Encode to Base64"
              description="We encode the decimal values to Base64 using the standard encoding process."
              content={
                <div>
                  <div className="mb-4">
                    <span className="font-semibold">Decimal values from byte grouping:</span>
                    <div className="mt-2 font-mono text-blue-600 dark:text-blue-400 flex flex-wrap justify-center">
                      {byteArray.map((byte, index) => (
                        <span key={index} className="m-1 p-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {byte}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="font-semibold">Convert decimal values to binary:</span>
                    <div className="mt-2 font-mono text-green-600 dark:text-green-400 flex flex-wrap justify-center">
                      {byteArray.map((byte, index) => (
                        <span key={index} className="m-1 p-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {byte.toString(2).padStart(8, '0')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="font-semibold">Group into 6-bit segments:</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Base64 uses 6-bit segments to represent 64 different values (2^6 = 64).</p>
                    <div className="mt-2 font-mono text-indigo-600 dark:text-indigo-400 flex flex-wrap justify-center">
                      {byteArray.map(byte => byte.toString(2).padStart(8, '0')).join('').match(/.{1,6}/g).map((segment, index) => (
                        <span key={index} className="m-1 p-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {segment.padEnd(6, '0')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Convert 6-bit segments to Base64 characters:</span>
                      <button
                        onClick={() => setShowBase64Table(!showBase64Table)}
                        className={`px-4 py-2 rounded ${darkMode
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                          } transition-colors duration-200`}
                      >
                        {showBase64Table ? 'Hide Base64 Table' : 'Show Base64 Table'}
                      </button>
                    </div>

                    <AnimatePresence>
                      {showBase64Table && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mb-4 overflow-hidden"
                        >
                          <Base64EncodingTable />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="w-full overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-100 dark:bg-gray-800">
                            <th className="border p-2">6-bit Segment</th>
                            <th className="border p-2">Decimal Value</th>
                            <th className="border p-2">Base64 Character</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byteArray.map(byte => byte.toString(2).padStart(8, '0')).join('').match(/.{1,6}/g).map((segment, index) => {
                            const decimalValue = parseInt(segment.padEnd(6, '0'), 2);
                            const base64Char = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'[decimalValue];
                            return (
                              <tr key={index} className="text-center">
                                <td className="border p-2 font-mono">{segment.padEnd(6, '0')}</td>
                                <td className="border p-2 font-semibold text-blue-600 dark:text-blue-400">{decimalValue}</td>
                                <td className="border p-2 font-mono font-semibold text-green-600 dark:text-green-400">{base64Char}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-6">
                      <span className="font-semibold">Final Base64 encoding:</span>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {base64Value.endsWith('=') ? "Padding ('=') is added if the number of bytes is not divisible by 3." : "No padding needed as the number of bytes is divisible by 3."}
                      </p>
                      <div className="mt-2 text-center">
                        <span className="font-mono font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900 px-4 py-2 rounded inline-block">
                          {base64Value}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900 rounded-lg shadow-md">
              <h4 className="text-xl font-semibold mb-4 text-blue-800 dark:text-blue-200">Important Notes:</h4>
              <ul className="list-disc list-inside space-y-2 text-blue-700 dark:text-blue-300">
                <li>This method treats the decimal as a 32-bit integer.</li>
                <li>The encoding process converts the integer to its binary representation, then directly to Base64.</li>
                <li>This approach ensures consistent results with standard Base64 encoding libraries.</li>
                <li>We are currently working on adding all possible encryption in a new section.</li>
              </ul>
            </div>
          </div>
        );
      }

      else {

        if (isNaN(decimalValue) || decimalValue < 0) {
          return (
            <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              <p className="text-red-500">Please enter a valid positive integer to see the conversion process.</p>
            </div>
          );
        }

        // Perform the conversion steps
        const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
        const steps = [];
        let remainingValue = decimalValue;
        let position = 0;

        while (remainingValue > 0) {
          const remainder = remainingValue % 64;
          const base64Char = base64Chars[remainder];
          steps.push({ position, remainder, base64Char, value: remainingValue });
          remainingValue = Math.floor(remainingValue / 64);
          position++;
        }

        // Construct the Base-64 value
        const base64Value = steps.length > 0 ? steps.map(step => step.base64Char).reverse().join('') : 'A';
        const base64Digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'.split('');

        // Embedded Base-64 Digit Grid
        const Base64DigitGrid = () => (
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-2">Base-64 Digits:</h4>
            <div className="grid grid-cols-8 gap-2 md:grid-cols-12 lg:grid-cols-16">
              {base64Digits.map((digit, index) => (
                <div
                  key={digit}
                  className={`p-2 text-center rounded ${index < 26
                    ? 'bg-blue-100 text-blue-800'
                    : index < 52
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                    } ${darkMode ? 'dark:bg-gray-700 dark:text-white' : ''}`}
                >
                  <span className="font-bold">{digit}</span>
                  <span className="text-xs block">{index}</span>
                </div>
              ))}
            </div>
          </div>
        );


        return (
          <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            {renderHowItWorks()}
            {renderExampleOrConversionText()}
            


            <p className="mb-4">
              To convert a decimal number to base-64, we repeatedly divide the number by 64 and keep track of the remainders. The remainders correspond to Base-64 digits based on the following mapping:
            </p>

            {/* Toggle Button for Base-64 Digit Grid */}
            <button
              onClick={() => setShowBase64Digits(!showBase64Digits)}
              className={`mb-4 px-4 py-2 rounded ${darkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                } transition-colors duration-200`}
            >
              {showBase64Digits ? 'Hide Base-64 Digits' : 'Show Base-64 Digits'}
            </button>

            {showBase64Digits && <Base64DigitGrid />}

            {steps.length > 0 ? (
              <>
                <div className="w-full overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="px-4 py-2">Step</th>
                        <th className="px-4 py-2">Calculation</th>
                        <th className="px-4 py-2">Remainder</th>
                        <th className="px-4 py-2">Base-64 Digit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {steps.map((step, index) => (
                        <tr
                          key={index}
                          className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-white') : (darkMode ? 'bg-gray-800' : 'bg-gray-50')}
                        >
                            <td className="py-2 px-4 border-b whitespace-nowrap">{steps.length - index}</td>
                            <td className="py-2 px-4 border-b break-all">{step.value} ÷ 64 = {Math.floor(step.value / 64)} remainder {step.remainder}</td>
                            <td className="py-2 px-4 border-b whitespace-nowrap">{step.remainder}</td>
                            <td className="py-2 px-4 border-b whitespace-nowrap">{step.base64Char}</td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6">
                  <h4 className="text-lg font-semibold mb-2">Base-64 Representation of {decimalValue}:</h4>
                  <div className="flex flex-wrap items-center">
                    {base64Value.split('').map((char, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="w-10 h-10 m-1 flex items-center justify-center border-2 border-purple-500 text-purple-500 font-bold text-xl rounded-md"
                      >
                        {char}
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Therefore Statement */}
                {renderThereforeStatement(decimalValue, fromUnit, base64Value, toUnit)}

                {/* Quiz Section */}
                <button
                  onClick={() => setShowQuiz(!showQuiz)}
                  className={`mt-6 mb-4 px-4 py-2 rounded ${darkMode
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-200 hover:bg-green-300 text-green-800'
                    } transition-colors duration-200`}
                >
                  {showQuiz ? 'Hide Quiz' : 'Take a Quiz'}
                </button>

                {showQuiz && (
                  <div className={`mb-6 p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-md`}>
                    <h4 className="font-semibold mb-2">Quick Quiz:</h4>
                    <p>Convert <span className="font-bold">{quizNumber}</span> to Base-64:</p>
                    <input
                      type="text"
                      value={quizAnswer}
                      onChange={(e) => setQuizAnswer(e.target.value)}
                      className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'} rounded-md w-full`}
                      placeholder="Enter Base-64 representation (e.g., KVy)"
                    />
                    <button
                      onClick={handleQuizSubmit}
                      className={`mt-2 px-4 py-1 rounded-md ${darkMode
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-200 hover:bg-blue-300 text-blue-800'
                        } transition-colors duration-200`}
                    >
                      Submit
                    </button>
                    {quizResult !== null && (
                      <p className={`mt-2 ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
                        {quizResult ? 'Correct!' : `Incorrect. The correct Base-64 representation is ${specialConversions['Base-64'].fromDecimal(quizNumber)}.`}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-red-500">Please enter a valid positive integer to see the conversion process.</p>
            )}
          </div>
        );
      }
    } else if (conversionType === 'number' && fromUnit === 'Decimal' && toUnit === 'Roman Numeral') {
      const decimalValue = exampleValue ? parseInt(exampleValue) : 42;
      const isExample = !inputValue;

      const romanNumerals = [
        { value: 1000, numeral: 'M' },
        { value: 900, numeral: 'CM' },
        { value: 500, numeral: 'D' },
        { value: 400, numeral: 'CD' },
        { value: 100, numeral: 'C' },
        { value: 90, numeral: 'XC' },
        { value: 50, numeral: 'L' },
        { value: 40, numeral: 'XL' },
        { value: 10, numeral: 'X' },
        { value: 9, numeral: 'IX' },
        { value: 5, numeral: 'V' },
        { value: 4, numeral: 'IV' },
        { value: 1, numeral: 'I' }
      ];

      let remainingValue = decimalValue;
      let romanValue = '';
      const steps = [];

      for (let i = 0; i < romanNumerals.length; i++) {
        const { value, numeral } = romanNumerals[i];
        while (remainingValue >= value) {
          romanValue += numeral;
          remainingValue -= value;
          steps.push({
            decimal: remainingValue + value,
            subtracted: value,
            remaining: remainingValue,
            added: numeral
          });
        }
      }

      const generateQuizNumber = () => {
        return Math.floor(Math.random() * 3999) + 1; // Random number between 1 and 3999
      };

      const convertToRoman = (num) => {
        let result = '';
        for (let i = 0; i < romanNumerals.length; i++) {
          while (num >= romanNumerals[i].value) {
            result += romanNumerals[i].numeral;
            num -= romanNumerals[i].value;
          }
        }
        return result;
      };

      return (
        <div className="space-y-4 p-4">
          {renderHowItWorks()}
          {renderExampleOrConversionText()}
          <p className="mb-4">
            To convert a decimal number to Roman numerals, we repeatedly subtract the largest possible Roman numeral value
            and add the corresponding symbol to our result. We continue this process until the remaining value reaches zero.
          </p>
          {steps.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">Step</th>
                      <th className="px-4 py-2">Decimal Value</th>
                      <th className="px-4 py-2">Subtracted</th>
                      <th className="px-4 py-2">Remaining</th>
                      <th className="px-4 py-2">Added</th>
                      <th className="px-4 py-2">Current Roman Numeral</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((step, index) => (
                      <tr key={index} className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}>
                        <td className="border px-4 py-2">{index + 1}</td>
                        <td className="border px-4 py-2">{step.decimal}</td>
                        <td className="border px-4 py-2">{step.subtracted}</td>
                        <td className="border px-4 py-2">{step.remaining}</td>
                        <td className="border px-4 py-2">{step.added}</td>
                        <td className="border px-4 py-2">
                          <span className={`font-bold ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                            {formatRomanNumeral(romanValue.slice(0, romanValue.indexOf(step.added) + step.added.length))}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                The final Roman numeral representation of{' '}
                <span className="break-all">{decimalValue}</span> is:{' '}
                <span className={`font-bold ${darkMode ? 'text-indigo-300' : 'text-indigo-600'} break-all`}>
                  {romanValue}
                </span>
              </p>

              <div className="mt-4 flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowFunFact(!showFunFact);
                    if (!showFunFact) {
                      setCurrentFunFact(getRandomFunFact());
                      setFunFactKey(prev => prev + 1);
                    }
                  }}
                  className="px-2 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  {showFunFact ? 'Hide Fun Fact' : 'Fun Fact'}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowQuiz(!showQuiz);
                    if (!showQuiz) {
                      setQuizNumber(generateQuizNumber());
                      setQuizAnswer('');
                      setQuizResult(null);
                    }
                  }}
                  className="px-2 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  {showQuiz ? 'Hide Quiz' : 'Quiz Yourself'}
                </motion.button>
              </div>

              {showFunFact && (
                <div className="mt-4 p-4 bg-gray-100 text-gray-800 rounded-md">
                  <h4 className="font-semibold">Fun Fact:</h4>
                  <p>{currentFunFact}</p>
                </div>
              )}

              {showQuiz && (
                <div className="mt-4 p-4 bg-gray-100 text-gray-800 rounded-md">
                  <h4 className="font-semibold">Quick Quiz:</h4>
                  <p>What's the decimal equivalent of {convertToRoman(quizNumber)}?</p>
                  <input
                    type="text"
                    value={quizAnswer}
                    onChange={(e) => setQuizAnswer(e.target.value)}
                    className="mt-2 px-2 py-1 border border-gray-300 rounded-md"
                  />
                  <button
                    onClick={handleQuizSubmit}
                    className="ml-2 px-4 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Submit
                  </button>
                  {quizResult !== null && (
                    <p className={`mt-2 ${quizResult ? 'text-green-600' : 'text-red-600'}`}>
                      {quizResult ? 'Correct!' : `Try again! The correct answer is ${quizNumber}.`}
                    </p>
                  )}
                </div>
              )}
              {renderThereforeStatement(decimalValue, fromUnit, romanValue, toUnit)}
            </>
          ) : (
            <p className="text-red-500">Please enter a valid positive integer to see the conversion process.</p>
          )}
        </div>
      );
    }

    else if (conversionType === 'number' && fromUnit === 'Decimal' && toUnit === 'Scientific Notation') {
      const decimalValue = exampleValue ? parseInt(exampleValue) : 42314.159;
      const isExample = !inputValue;

      const scientificNotation = decimalValue.toExponential();
      const [coefficient, exponent] = scientificNotation.split('e');
      const adjustedCoefficient = parseFloat(coefficient).toFixed(4);
      const adjustedExponent = parseInt(exponent);

      const steps = [
        {
          step: 1,
          description: "Identify the most significant digit",
          detail: `The most significant digit is ${decimalValue.toString()[0]}`,
          visual: (
            <span className="font-mono">
              <span className="font-bold text-red-700">{decimalValue.toString()[0]}</span>
              {decimalValue.toString().slice(1)}
            </span>
          )
        },
        {
          step: 2,
          description: "Move the decimal point",
          detail: `Move the decimal point ${Math.abs(adjustedExponent)} place${Math.abs(adjustedExponent) !== 1 ? 's' : ''} ${adjustedExponent > 0 ? 'left' : 'right'}`,
          visual: (
            <span className="font-mono">
              {decimalValue.toString()[0]}.
              {decimalValue.toString().slice(1, 5)}
              <span className="text-blue-700 font-bold">
                {adjustedExponent > 0 ? '←'.repeat(Math.abs(adjustedExponent)) : '→'.repeat(Math.abs(adjustedExponent))}
              </span>
              {decimalValue.toString().slice(5)}
            </span>
          )
        },
        {
          step: 3,
          description: "Write the coefficient",
          detail: `The coefficient becomes ${adjustedCoefficient}`,
          visual: (
            <span className="font-mono font-bold text-green-700">{adjustedCoefficient}</span>
          )
        },
        {
          step: 4,
          description: "Determine the exponent",
          detail: `The exponent is ${adjustedExponent}`,
          visual: (
            <span className="font-mono">
              × 10<sup className="font-bold text-purple-700">{adjustedExponent}</sup>
            </span>
          )
        },
        {
          step: 5,
          description: "Combine the parts",
          detail: `Combine as ${adjustedCoefficient} × 10^${adjustedExponent}`,
          visual: (
            <span className="font-mono text-xl font-bold">
              <span className="text-green-700">{adjustedCoefficient}</span> × 10<sup className="text-purple-700">{adjustedExponent}</sup>
            </span>
          )
        },
      ];

      return (
        <div className="space-y-4 p-4">
          {renderHowItWorks()}
          {renderExampleOrConversionText()}
          <div className={`mb-6 p-4 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'} rounded-lg`}>
            <h4 className="font-semibold mb-2">What is Scientific Notation?</h4>
            <p>
              Scientific notation is a way of expressing numbers that are too large or too small
              to be conveniently written in decimal form. It's written in the form a × 10^n, where
              1 &le; |a| &lt; 10 and n is an integer.
            </p>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Conversion Process:</h4>
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`mb-4 p-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg overflow-hidden`}
              >
                <h5 className="font-semibold flex items-center">
                  <span className={`w-8 h-8 flex-shrink-0 rounded-full ${
                    darkMode ? 'bg-blue-500' : 'bg-blue-100'
                  } flex items-center justify-center mr-2`}>
                    {step.step}
                  </span>
                  <span className="break-words">{step.description}</span>
                </h5>
                <p className="mb-2 break-all">{step.detail}</p>
                <div className={`p-3 rounded-lg ${
                  darkMode ? 'bg-gray-900' : 'bg-gray-50'
                } text-center font-mono break-all overflow-x-auto`}>
                  {step.visual}
                </div>
              </motion.div>
            ))}
          </div>

          <div className={`mb-6 p-4 ${darkMode ? 'bg-green-900' : 'bg-green-100'} rounded-lg`}>
            <h4 className="font-semibold mb-2">Final Result:</h4>
            <div className="text-center">
              <p className={`text-3xl font-bold ${darkMode ? 'text-green-400' : 'text-green-700'} break-all`}>
                {adjustedCoefficient} × 10<sup>{adjustedExponent}</sup>
              </p>
              <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'} break-all`}>
                {decimalValue.toLocaleString()} in standard notation
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Why is Scientific Notation Important?</h4>
            <ul className="list-disc list-inside space-y-2">
              <li>Simplifies very large or very small numbers</li>
              <li>Makes multiplication and division of extreme numbers easier</li>
              <li>Standardizes number representation in scientific fields</li>
              <li>Helps in understanding the scale and order of magnitude of numbers</li>
              <li>Facilitates calculations in fields like astronomy, physics, and chemistry</li>
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Real-world Applications:</h4>
            <ul className="list-disc list-inside space-y-2">
              <li>Astronomy: Distance to Andromeda Galaxy ≈ 2.537 × 10^22 meters</li>
              <li>Physics: Planck's constant ≈ 6.626 × 10^-34 joule-seconds</li>
              <li>Chemistry: Avogadro's number ≈ 6.022 × 10^23 particles per mole</li>
              <li>Biology: Number of cells in human body ≈ 3.72 × 10^13</li>
              <li>Computer Science: Storage capacity of 1 TB ≈ 1 × 10^12 bytes</li>
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Quick Tips:</h4>
            <ul className="list-disc list-inside space-y-2">
              <li>To multiply numbers in scientific notation, multiply the coefficients and add the exponents</li>
              <li>To divide, divide the coefficients and subtract the exponents</li>
              <li>When adding or subtracting, convert to the same exponent first</li>
              <li>Remember: 10^0 = 1, so 5 × 10^0 is just 5 in standard notation</li>
            </ul>
          </div>

          <div className="mb-6">
            <button
              onClick={() => setShowQuiz(!showQuiz)}
              className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showQuiz ? 'Hide Quiz' : 'Test Your Knowledge'}
            </button>
          </div>

          {showQuiz && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-md`}
            >
              <h4 className="font-semibold mb-2">Quick Quiz:</h4>
              <p>Convert {(Math.random() * 1000000).toFixed(2)} to scientific notation:</p>
              <input
                type="text"
                value={quizAnswer}
                onChange={(e) => setQuizAnswer(e.target.value)}
                className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} rounded-md w-full`}
                placeholder="Enter your answer (e.g., 1.23 × 10^4)"
              />
              <button
                onClick={handleQuizSubmit}
                className={`mt-2 px-4 py-1 ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md transition-colors`}
              >
                Submit
              </button>
              {quizResult !== null && (
                <p className={`mt-2 ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
                  {quizResult ? 'Correct!' : 'Try again! Remember the format: a × 10^n where 1 ≤ |a| < 10'}
                </p>
              )}
            </motion.div>
          )}
          {renderThereforeStatement(decimalValue, fromUnit, `${adjustedCoefficient} × 10^${adjustedExponent}`, toUnit)}
        </div>
      );
    }

    // ... (rest of the conversion types)
  };

  // Calculate steps and set them ready together
  useEffect(() => {
    setStepsReady(false);
    
    const calculateSteps = () => {
      // Calculate ALL steps here first
      let allSteps = [];
      
      if (fromUnit === 'Decimal') {
        if (toUnit === 'Base-64') {
          // Calculate Base-64 steps
          const decimalValue = isExample ? 42 : parseInt(inputValue, 10);
          let remainingValue = decimalValue;
          while (remainingValue > 0) {
            const remainder = remainingValue % 64;
            const base64Char = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'[remainder];
            allSteps.push({ value: remainingValue, remainder, base64Char });
            remainingValue = Math.floor(remainingValue / 64);
          }
        }
        // Add other conversion types here...
      }
      
      // Set all steps at once
      setSteps(allSteps);
      // Only set stepsReady to true after ALL calculations are done
      setStepsReady(true);
    };

    // Small delay to ensure clean render
    const timer = setTimeout(calculateSteps, 50);
    return () => clearTimeout(timer);
  }, [inputValue, fromUnit, toUnit, isExample]);

  // Only render when ALL steps are ready
  if (!stepsReady) {
    return (
      <div className="space-y-4 w-full max-w-3xl mx-auto p-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
    );
  }

  // Render everything at once when ready
  return (
    <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      {getExplanation()}
    </div>
  );
};

export default ConversionExplanationDecimalToAll;